import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { TestCaseDocumentPresentation } from '../models/test-case-document-presentation';
import { TestCaseEntity } from '../models/test-case-entity';
import { TestCasePresentation } from '../models/test-case-presentation';
import { PresentationTestRunResult } from '../models/test-run-presentation';
import { DownloaderService } from '../../shared/download/downloader.service';
import { catchError, map } from 'rxjs/operators';
import { TestRunDocumentFile } from '../models/test-run-document-file';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { AvailableAdditionalFileTypeStatus } from '../models/test-case-available-additional-file-types';
import { TestCaseInterchangeDto } from '../models/test-case-interchange-dto';
import { TestFileExpectedOutputDto } from '../models/test-file-expected-output-dto';
import { TestRunResultDto } from '../models/test-run-result-dto';
import { InitConfiguration } from '../../distribution/models/init-configuration';
import { AvailableInterchangeOutStatus } from '../models/test-case-available-interchange-out';
import { DataFieldValue } from '../../shared/datafields/models/datafield-value';
import { DataFieldGroup } from '../../shared/datafields/models/datafield-group';
import { CompareRule } from '../models/test-tool-compare-rule';
import { AcceptStateDto } from '../models/test-run-accept-state';

@Injectable({
    providedIn: 'root'
})
export class TestToolService {
    constructor(private httpService: JsonHttpService, private downloaderServicer: DownloaderService) { }

    getTestCases(): Promise<TestCasePresentation[]> {
        return this.httpService.getTyped<TestCasePresentation[]>('/api/test-tool/test-case/get-test-cases-for-presentation').toPromise();
    }

    saveTestCase(item: TestCaseEntity): Promise<TestCaseEntity> {
        return this.httpService.post('/api/test-tool/test-case/save-test-case', item).toPromise();
    }

    isTestCaseNameAvailable(testCaseName: string): Promise<boolean> {
        const options = { params: new HttpParams().set('testCaseName', `${testCaseName}`) };
        return this.httpService.get('/api/test-tool/test-case/is-test-case-name-available', options).toPromise();
    }

    addInterchangesToTestCase(testCaseId: number, interchangeIds: number[], expectedOutputType: number): Promise<void> {
        return this.httpService.post(`/api/test-tool/test-case/${testCaseId}/add-interchanges`, { interchangeIds, expectedOutputType }, null, false).toPromise();
    }

    addDocumentsToTestCase(testCaseId: number, documentIds: number[], expectedOutputType: number): Promise<void> {
        return this.httpService.post(`/api/test-tool/test-case/${testCaseId}/add-documents`, { documentIds, expectedOutputType }).toPromise();
    }

    getTestCaseDocumentsForTestCase(testCaseId: number): Promise<any[]> {
        return this.httpService.get(`/api/test-tool/test-case/${testCaseId}/get-test-case-documents`).toPromise();
    }

    getTestCaseInterchangesForTestCase(testCaseId: number): Promise<TestCaseInterchangeDto[]> {
        return this.httpService.get(`/api/test-tool/test-case/${testCaseId}/get-test-case-interchanges`).toPromise();
    }

    getTestCaseInterchangeExpectedOutputFiles(testCaseInterchangeId: number): Promise<TestFileExpectedOutputDto[]> {
        const options = { params: new HttpParams().set('testCaseInterchangeId', `${testCaseInterchangeId}`) };
        return this.httpService.get('/api/test-tool/test-case/get-test-case-interchange-expected-output-files', options).toPromise();
    }

    saveTestCaseDocumentForTestCase(item: TestCaseDocumentPresentation): Promise<TestCaseDocumentPresentation> {
        return this.httpService.post('/api/test-tool/test-case/save-test-case-document', item).toPromise();
    }

    saveTestCaseInterchange(item: TestCaseInterchangeDto): Promise<TestCaseInterchangeDto> {
        return this.httpService.post('/api/test-tool/test-case/save-test-case-interchange', item).toPromise();
    }

    saveTestFileExpectedOutput(item: TestFileExpectedOutputDto): Promise<TestFileExpectedOutputDto> {
        return this.httpService.post('/api/test-tool/test-case/save-test-case-interchange-expected-output-file', item).toPromise();
    }

    saveTestFileExpectedOutputs(items: TestFileExpectedOutputDto[]): Promise<TestFileExpectedOutputDto[]> {
        return this.httpService.post('/api/test-tool/test-case/save-test-case-interchange-expected-output-files', items).toPromise();
    }

    updateTestCaseDocumentsEncoding(testCaseDocumentPresentations: TestCaseDocumentPresentation[], encodingId: number | null): Promise<void> {
        return this.httpService.post('/api/test-tool/test-case/update-test-case-documents-encoding', { testCaseDocumentPresentations, encodingId }).toPromise();
    }

    deleteTestCaseInterchangeExpectedOuputFile(testCaseInterchangeExpectedOutputFileId: number): Promise<void> {
        const options = { params: new HttpParams().set('testCaseInterchangeExpectedOutputFileId', `${testCaseInterchangeExpectedOutputFileId}`) };
        return this.httpService.post('/api/test-tool/test-case/delete-test-case-interchange-expected-output-file', null, options).toPromise();
    }

    deleteTestCaseInterchange(testCaseInterchangeId: number, useGenericErrorHandling: boolean = true): Promise<void> {
        const options = { params: new HttpParams().set('testCaseInterchangeId', `${testCaseInterchangeId}`) };
        return this.httpService.post('/api/test-tool/test-case/delete-test-case-interchange', null, options, useGenericErrorHandling).toPromise();
    }

    deleteTestCaseDocument(testCaseDocumentId: number): Promise<void> {
        const options = { params: new HttpParams().set('testCaseDocumentId', `${testCaseDocumentId}`) };
        return this.httpService.post('/api/test-tool/test-case/delete-test-case-document', null, options).toPromise();
    }

    deleteTestCaseDocuments(testCaseDocumentIds: number[]): Promise<void> {
        return this.httpService.post('/api/test-tool/test-case/delete-test-case-documents', { TestCaseDocumentIds: testCaseDocumentIds }).toPromise();
    }

    deleteTestCase(testCaseId: number): Promise<void> {
        const options = { params: new HttpParams().set('testCaseId', `${testCaseId}`) };
        return this.httpService.post('/api/test-tool/test-case/delete-test-case', null, options).toPromise();
    }

    deleteTestRun(testRunId: number): Promise<void> {
        const options = { params: new HttpParams().set('testRunId', `${testRunId}`) };
        return this.httpService.post('/api/test-tool/test-run/delete-test-run', null, options).toPromise();
    }

    addRandomDocumentsToTestCase(testCaseId: number, selectedFromDate: string, selectedToDate: string, maxNoOfDocumentsPrObject: number, documentTypeId: number | null | undefined): Promise<number> {
        return this.httpService.post(`/api/test-tool/test-case/${testCaseId}/add-random-documents`, { selectedFromDate, selectedToDate, maxNoOfDocumentsPrObject, documentTypeId }).toPromise();
    }

    getInterchangeOutAvailability(interchangeIds: number[]): Promise<AvailableInterchangeOutStatus> {
        return this.httpService.post('api/test-tool/test-case/get-interchange-out-availability', { Ids: interchangeIds }).toPromise();
    }

    getExpectedOutputAdditionalFileAvailability(interchangeIds: number[]): Promise<AvailableAdditionalFileTypeStatus> {
        return this.httpService.post('api/test-tool/test-case/get-expected-output-additional-file-availability', { Ids: interchangeIds }).toPromise();
    }

    rerunTestRun(testRunId: number): Promise<number> {
        const options = { params: new HttpParams().set('testRunId', `${testRunId}`) };
        return this.httpService.post('/api/test-tool/test-run/rerun-test-run', null, options).toPromise();
    }

    runTestCases(testCases: number[], comment: string): Promise<void> {
        const options = { params: new HttpParams().set('comment', comment) };
        return this.httpService.post('/api/test-tool/test-run/run-test-cases', testCases, options).toPromise();
    }

    getTestRuns(): Promise<PresentationTestRunResult[]> {
        return this.httpService.get('/api/test-tool/test-run/get-test-runs-for-presentation').toPromise();
    }

    getTestRunByTestRunId(testRunId: number) : Promise<PresentationTestRunResult> {
        return this.httpService.get(`/api/test-tool/test-run/get-test-run-for-presentation/${testRunId}`).toPromise();
    }

    getTestRunResults(testRunId: number): Promise<TestRunResultDto[]> {
        return this.httpService.get(`/api/test-tool/test-run/${testRunId}/get-test-run-results`).toPromise();
    }

    compareTestRun(testRunId: number): Promise<void> {
        const options = { params: new HttpParams().set('testRunId', `${testRunId}`) };
        return this.httpService.post('/api/test-tool/test-run/compare', null, options).toPromise();
    }

    downloadTestCaseInterchange(testCaseInterchangeId: number): Promise<void> {
        const options = { params: new HttpParams().set('testCaseInterchangeId', `${testCaseInterchangeId}`) };
        return this.httpService.get('api/test-tool/test-case/get-test-case-interchange-download-token', options).toPromise().then((token) => this.downloaderServicer.downloadFile(token));
    }

    downloadTestCaseDocumentInterchangeOut(testCaseDocumentId: number): Promise<void> {
        const options = { params: new HttpParams().set('testCaseDocumentId', `${testCaseDocumentId}`) };
        return this.httpService.get('api/test-tool/test-case/get-test-case-document-download-token', options).toPromise().then((token) => this.downloaderServicer.downloadFile(token));
    }

    downloadAllTestRunFiles(testRunDocumentId: number): Promise<void> {
        const options = { params: new HttpParams().set('testRunDocumentId', `${testRunDocumentId}`) };
        return this.httpService.get('api/test-tool/test-run/get-test-run-document-download-token', options).toPromise().then((token) => this.downloaderServicer.downloadFile(token));
    }

    saveTestCaseInterchangeInitConfig(testCaseInterchangeId: number, initConfig: InitConfiguration): Promise<InitConfiguration> {
        const options = { params: new HttpParams().set('testCaseInterchangeId', testCaseInterchangeId.toString()) };
        return this.httpService.post('/api/test-tool/test-case/save-test-case-interchange-init-config', initConfig, options).toPromise();
    }

    deleteTestCaseInterchangeInitConfig(testCaseInterchangeId: number) {
        const options = { params: new HttpParams().set('testCaseInterchangeId', testCaseInterchangeId.toString()) };
        return this.httpService.post('/api/test-tool/test-case/delete-test-case-interchange-init-config', null, options).toPromise();
    }

    getTestCaseInterchangeInitConfig(testCaseInterchangeId: number): Promise<InitConfiguration> {
        const options = { params: new HttpParams().set('testCaseInterchangeId', testCaseInterchangeId.toString()) };
        return this.httpService.get('/api/test-tool/test-case/get-test-case-interchange-init-config', options).toPromise();
    }

    async getTestRunDocumentFile(testRunDocumentId: number, actual: boolean, parsed: boolean): Promise<TestRunDocumentFile> {
        const options = {
            params: new HttpParams()
                .set('testRunDocumentId', `${testRunDocumentId}`)
                .set('actual', `${actual}`)
                .set('parsed', `${parsed}`)
        };

        return this.httpService.getTyped<TestRunDocumentFile>('api/test-tool/test-run/get-test-run-document-file', options, false).pipe(
            map(result => new TestRunDocumentFile(result)),
            catchError(error => error.status === 404 ? of<TestRunDocumentFile>(null) : throwError(error)),
        ).toPromise();
    }

    getAcceptStatesForTestRunInterchange(testRunInterchangeId: number): Promise<AcceptStateDto[]> {
        const options = { params: new HttpParams().set('testRunInterchangeId', `${testRunInterchangeId}`) };
        return firstValueFrom(this.httpService.getTyped<AcceptStateDto[]>('/api/test-tool/test-run/get-accept-states-for-test-run-interchange', options));
    }

    getAcceptStateForTestRunDocument(testRunDocumentId: number): Promise<AcceptStateDto> {
        const options = { params: new HttpParams().set('testRunDocumentId', `${testRunDocumentId}`) };
        return firstValueFrom(this.httpService.getTyped<AcceptStateDto>('/api/test-tool/test-run/get-accept-state-for-test-run-document', options));
    }

    updateAcceptState(acceptStates: AcceptStateDto[]): Promise<AcceptStateDto[]> {
        return firstValueFrom(this.httpService.postTyped<AcceptStateDto[]>('/api/test-tool/test-run/update-accept-states', acceptStates));
    }

    /** Compare Rules */

    saveCompareRule(item: CompareRule, dataFieldValues: DataFieldValue[]): Promise<CompareRule> {
        const saveObject = {
            ComparePlugin: item,
            Values: dataFieldValues
        };
        return this.httpService.post('/api/test-tool/compare-plugin/save', saveObject).toPromise();
    }

    cloneCompareRule(compareRuleId: number): Promise<CompareRule> {
        const options = { params: new HttpParams().set('comparePluginId', compareRuleId.toString()) };
        return this.httpService.get('/api/test-tool/compare-plugin/clone', options).toPromise();
    }

    deleteCompareRule(id: number, useGenericErrorHandling: (err: any, caught: Observable<any>) => boolean = _ => true): Promise<void> {
        const options = { params: new HttpParams().set('comparePluginsId', id.toString()) };
        return this.httpService.postWithErrorEval('/api/test-tool/compare-plugin/delete', null, options, useGenericErrorHandling).toPromise();
    }

    getCompareRule(): Promise<CompareRule[]> {
        return this.httpService.get('/api/test-tool/compare-plugin/get').toPromise();
    }

    getCompareRuleDataFieldGroups(artifactId: string, compareRuleId?: number): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        if (compareRuleId) {
            options.params = options.params.set('comparePluginId', compareRuleId);
        }
        return this.httpService.get('/api/test-tool/compare-plugin/get-datafields', options).toPromise();
    }
}
